import { listAppointments } from '../services/appointments';
import { fetchMeasurement } from '../services/measurements';
import { fetchPatient } from '../services/patients';

export const alertMeasurementTypeMap = {
    STAGE_2_HYPERTENSION: 'BLOOD_PRESSURE',
    SEVERE_HYPERTENSION: 'BLOOD_PRESSURE',
    HIGH_GLUCOSE: 'BLOOD_SUGAR',
    LOW_GLUCOSE: 'BLOOD_SUGAR',
    LOW_OXYGEN: 'BLOOD_OXYGEN',
    HIGH_HR_AGE: 'HEARTRATE'
};

export const buildMissedCallRemoteUser = (notification) => {
    const data = notification?.data || {};
    const userId = data?.uuid || '';

    if (!userId) {
        return null;
    }

    return {
        userId,
        displayName: data?.name || 'Utilizador',
        name: data?.name || 'Utilizador',
        role: data?.role || '',
        clinicianType: data?.clinician_type || ''
    };
};

const enrichAppointmentNotification = async ({ notification, userRoleUuid }) => {
    const appointmentUuid = notification?.data?.appointment || '';

    if (!appointmentUuid || !userRoleUuid) {
        return notification;
    }

    try {
        const response = await listAppointments({
            userRoleUuid,
            clinicianUuid: userRoleUuid,
            queryTs: notification?.not_before || undefined,
            paginationLimit: 50
        });

        const appointments = Array.isArray(response?.data) ? response.data : [];
        const appointment = appointments.find((item) => item?.uuid === appointmentUuid) || null;
        const caregiverName = appointment?.caregiver?.name || '';
        const referenceTs = appointment?.ts || notification?.not_before || null;

        return {
            ...notification,
            _displayContext: {
                ...(notification?._displayContext || {}),
                subjectName: caregiverName,
                referenceTs
            }
        };
    } catch {
        return notification;
    }
};

const enrichAlertNotification = async ({ notification, userRoleUuid }) => {
    const measurementUuid = notification?.data?.measurement || '';
    const alertCode = String(notification?.data?.alert || '').toUpperCase();

    if (!measurementUuid || !userRoleUuid) {
        return notification;
    }

    try {
        const measurementResponse = await fetchMeasurement({
            userRoleUuid,
            measurementUuid
        });

        const measurement = measurementResponse?.data || null;
        const subject = measurement?.subject || null;
        const referenceTs = measurement?.ts || notification?.not_before || null;

        if (!subject?.uuid) {
            return notification;
        }

        if (subject.role === 'PATIENT_CARE_GIVER') {
            return {
                ...notification,
                _displayContext: {
                    ...(notification?._displayContext || {}),
                    subjectName: subject.name || '',
                    referenceTs,
                    measurementType: alertMeasurementTypeMap[alertCode] || measurement?.dtype || 'BLOOD_PRESSURE'
                }
            };
        }

        if (subject.role === 'PATIENT') {
            const patientResponse = await fetchPatient({
                userRoleUuid,
                patientUuid: subject.uuid
            });

            const patient = patientResponse?.data || null;
            const caregivers = Array.isArray(patient?.caregivers) ? patient.caregivers : [];
            const linkedCaregiver = caregivers.find(
                (item) => String(item?.role || '').toUpperCase() === 'PATIENT_CARE_GIVER'
            );

            return {
                ...notification,
                _displayContext: {
                    ...(notification?._displayContext || {}),
                    subjectName: linkedCaregiver?.name || subject.name || '',
                    referenceTs,
                    measurementType: alertMeasurementTypeMap[alertCode] || measurement?.dtype || 'BLOOD_PRESSURE'
                }
            };
        }

        return notification;
    } catch {
        return notification;
    }
};

export const enrichNotificationsForDisplay = async ({ notifications, userRoleUuid }) => {
    const list = Array.isArray(notifications) ? notifications : [];

    return Promise.all(
        list.map(async (notification) => {
            const type = String(notification?.type || '').toUpperCase();

            if (type === 'APPT_REMINDER') {
                return enrichAppointmentNotification({ notification, userRoleUuid });
            }

            if (type === 'ALERT') {
                return enrichAlertNotification({ notification, userRoleUuid });
            }

            return notification;
        })
    );
};

export const resolveAppointmentNotificationTarget = async ({
    notification,
    userRoleUuid
}) => {
    const appointmentUuid = notification?.data?.appointment || '';
    const action = String(notification?.data?.action || '').toUpperCase();
    const notBefore = notification?.not_before || null;
    if (!appointmentUuid || !userRoleUuid) {
        return null;
    }

    let focusDateIso = notBefore;
    if (focusDateIso && action === 'REMINDER') {
        const date = new Date(focusDateIso);
        if (!Number.isNaN(date.getTime())) {
            date.setMinutes(date.getMinutes() + 30);
            focusDateIso = date.toISOString();
        }
    }

    return {
        navigateTo: '/appointments',
        navigationState: {
            agendaRoleUuid: userRoleUuid,
            focusAppointmentUuid: appointmentUuid,
            focusDateIso
        }
    };
};

export const resolveAlertNotificationTarget = async ({
    notification,
    userRoleUuid
}) => {
    const measurementUuid = notification?.data?.measurement || '';
    const alertCode = String(notification?.data?.alert || '').toUpperCase();

    if (!measurementUuid || !userRoleUuid) {
        return null;
    }

    const measurementResponse = await fetchMeasurement({
        userRoleUuid,
        measurementUuid
    });

    const measurement = measurementResponse?.data || null;
    const subject = measurement?.subject || null;
    const measurementType = alertMeasurementTypeMap[alertCode] || measurement?.dtype || 'BLOOD_PRESSURE';

    if (!subject?.uuid) {
        return null;
    }

    if (subject.role === 'PATIENT_CARE_GIVER') {
        return {
            measurement,
            caregiverRoleUuid: subject.uuid,
            measurementType
        };
    }

    if (subject.role === 'PATIENT') {
        const patientResponse = await fetchPatient({
            userRoleUuid,
            patientUuid: subject.uuid
        });

        const patient = patientResponse?.data || null;
        const caregivers = Array.isArray(patient?.caregivers) ? patient.caregivers : [];
        const linkedCaregiver = caregivers.find(
            (item) => String(item?.role || '').toUpperCase() === 'PATIENT_CARE_GIVER'
        );

        if (linkedCaregiver?.uuid) {
            return {
                measurement,
                caregiverRoleUuid: linkedCaregiver.uuid,
                measurementType
            };
        }

        return {
            measurement,
            measurementType,
            unresolvedPatientCaregiver: true,
            patientUuid: subject.uuid,
            patientName: subject.name || 'Paciente'
        };
    }

    return null;
};
